import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { useFetch } from "../../lib/useFetch";
import { useIntl } from "../../lib/i18n";
import { DraftChatPanel } from "../../components/DraftChatPanel";

type Draft = {
  id: string;
  matterId?: string | null;
  state: string;
  templateName?: string | null;
  bodyMarkdown: string;
  updatedAt: string;
};

type Deadline = {
  id: string;
  matterId: string;
  formKind: string;
  deadlineKind: string;
  deadlineDate: string;
  status: string;
};

type Citation = {
  text: string;
  kind: string;
  status: "VERIFIED" | "OUTDATED" | "UNVERIFIED" | "UNKNOWN" | "HALLUCINATED";
  sourceUrl?: string;
  position: number;
  note?: string;
  canonicalReplacement?: string;
};

type DraftWithExtras = Draft & {
  adversarialReviewMd?: string;
  adversarialAt?: string | null;
  citations?: Citation[];
  citationsScannedAt?: string | null;
};

export default function DraftDetailPage() {
  const { id = "" } = useParams();
  const { session } = useAuth();
  const { t } = useIntl();
  const { data, loading, error, refresh } = useFetch<DraftWithExtras>(
    `/api/v1/tenant/drafts/${encodeURIComponent(id)}`,
  );
  // The drafting controller writes LLM-extracted deadlines under the
  // draft's matter_id when L3 tool-use is wired; surface them here so
  // a paralegal sees what got auto-recorded without leaving the draft.
  const deadlinesQ = useFetch<{ deadlines: Deadline[] }>(
    data?.matterId ? "/api/v1/tenant/deadlines" : "/api/v1/tenant/me",
  );
  const matterDeadlines = (deadlinesQ.data?.deadlines ?? []).filter(
    (d) => data?.matterId && d.matterId === data.matterId,
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState(false);

  const transition = async (action: "approve" | "reject" | "sent") => {
    setActionPending(true);
    setActionError(null);
    try {
      const res = await api(`/api/v1/tenant/drafts/${encodeURIComponent(id)}/${action}`, {
        method: "POST",
        token: session?.token,
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
      refresh();
    } catch (err) {
      setActionError(String((err as Error).message ?? err));
    } finally {
      setActionPending(false);
    }
  };

  const exportAs = (kind: "PDF" | "DOCX") => {
    if (!session?.token) return;
    const url = `/api/v1/tenant/drafts/${encodeURIComponent(id)}/render?kind=${kind}`;
    // Bearer auth + binary stream — open in a new tab via a temporary <a>
    // with the token in a query param substitution is unsafe; instead fetch
    // the blob and download it client-side.
    void (async () => {
      try {
        const res = await fetch(url, {
          method: "POST",
          headers: { Authorization: `Bearer ${session.token}` },
        });
        if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = `draft-${id}.${kind.toLowerCase()}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
      } catch (err) {
        setActionError(String((err as Error).message ?? err));
      }
    })();
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-brand-900">
        {t("drafts.title")} — {id}
      </h1>
      {loading && <p className="mt-4 text-slate-500">{t("common.loading")}</p>}
      {error && <p className="mt-4 text-rose-600">{error}</p>}
      {data && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
            <span className="rounded bg-slate-100 px-2 py-1">{data.state}</span>
            {data.templateName && (
              <span className="text-slate-500">{data.templateName}</span>
            )}
            {data.matterId && (
              <span className="text-slate-500">matter: {data.matterId}</span>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => transition("approve")}
              disabled={actionPending || data.state !== "PENDING_REVIEW"}
              className="rounded-md bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
            >
              {t("reviews.action.approve")}
            </button>
            <button
              type="button"
              onClick={() => transition("reject")}
              disabled={actionPending || data.state !== "PENDING_REVIEW"}
              className="rounded-md bg-rose-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-600 disabled:opacity-50"
            >
              Reject
            </button>
            <button
              type="button"
              onClick={() => transition("sent")}
              disabled={actionPending || data.state !== "APPROVED"}
              className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-500 disabled:opacity-50"
            >
              Mark sent
            </button>
            <span className="mx-2 text-slate-300">|</span>
            <button
              type="button"
              onClick={() => exportAs("PDF")}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Export PDF
            </button>
            <button
              type="button"
              onClick={() => exportAs("DOCX")}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Export DOCX
            </button>
          </div>
          {actionError && (
            <p className="mt-3 text-sm text-rose-600">{actionError}</p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={async () => {
                setActionPending(true);
                setActionError(null);
                try {
                  const res = await api(
                    `/api/v1/tenant/drafts/${encodeURIComponent(id)}/adversarial`,
                    { method: "POST", token: session?.token, body: JSON.stringify({}) },
                  );
                  if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
                  refresh();
                } catch (e) {
                  setActionError(String((e as Error).message ?? e));
                } finally {
                  setActionPending(false);
                }
              }}
              disabled={actionPending}
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
            >
              Run adversarial review
            </button>
            <button
              type="button"
              onClick={async () => {
                setActionPending(true);
                setActionError(null);
                try {
                  const res = await api(
                    `/api/v1/tenant/drafts/${encodeURIComponent(id)}/verify-citations`,
                    { method: "POST", token: session?.token, body: JSON.stringify({}) },
                  );
                  if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
                  refresh();
                } catch (e) {
                  setActionError(String((e as Error).message ?? e));
                } finally {
                  setActionPending(false);
                }
              }}
              disabled={actionPending}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Verify citations
            </button>
          </div>

          {data.citations && data.citations.length > 0 && (
            <section className="mt-6 rounded-md border border-slate-200 bg-white p-3">
              <h2 className="text-sm font-semibold text-brand-900">
                Citations ({data.citations.length})
                {data.citationsScannedAt && (
                  <span className="ml-2 text-xs font-normal text-slate-400">
                    scanned {new Date(data.citationsScannedAt).toLocaleString()}
                  </span>
                )}
              </h2>
              <ul className="mt-2 space-y-1 text-sm">
                {data.citations.map((c, i) => (
                  <li
                    key={i}
                    className={
                      c.status === "VERIFIED"
                        ? "rounded bg-emerald-50 px-3 py-1 ring-1 ring-emerald-200"
                        : c.status === "OUTDATED"
                          ? "rounded bg-amber-50 px-3 py-1 ring-1 ring-amber-400"
                          : c.status === "UNVERIFIED" || c.status === "UNKNOWN"
                            ? "rounded bg-amber-50 px-3 py-1 ring-1 ring-amber-300"
                            : "rounded bg-rose-50 px-3 py-1 ring-1 ring-rose-300"
                    }
                  >
                    <span className="font-mono">{c.text}</span>{" "}
                    <span className="text-xs text-slate-500">[{c.kind}]</span>{" "}
                    <span className="text-xs font-medium uppercase">{c.status}</span>
                    {c.note && <span className="ml-2 text-xs text-slate-500">— {c.note}</span>}
                    {c.canonicalReplacement && (
                      <CitationCanonicalPopover canonical={c.canonicalReplacement} />
                    )}
                    {c.sourceUrl && (
                      <a
                        href={c.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-2 text-xs text-brand-700 hover:underline"
                      >
                        source ›
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data.adversarialReviewMd && (
            <section className="mt-6 rounded-md border border-amber-300 bg-amber-50 p-3">
              <h2 className="text-sm font-semibold text-amber-900">
                Adversarial review — opposing counsel's strongest line of attack
                {data.adversarialAt && (
                  <span className="ml-2 text-xs font-normal text-amber-700">
                    generated {new Date(data.adversarialAt).toLocaleString()}
                  </span>
                )}
              </h2>
              <pre className="mt-3 max-h-[40vh] overflow-auto whitespace-pre-wrap rounded bg-white p-3 text-sm text-slate-700 ring-1 ring-amber-200">
                {data.adversarialReviewMd}
              </pre>
            </section>
          )}

          {data.matterId && matterDeadlines.length > 0 && (
            <section className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-3">
              <h2 className="text-sm font-semibold text-amber-900">
                Auto-extracted deadlines for this matter ({matterDeadlines.length})
              </h2>
              <p className="mt-1 text-xs text-amber-900/80">
                Recorded by the LLM during draft generation. These are real V016 rows under
                this matter; verify dates before relying on them.
              </p>
              <ul className="mt-3 space-y-1 text-sm">
                {matterDeadlines.map((d) => (
                  <li key={d.id} className="rounded bg-white px-3 py-2 ring-1 ring-amber-200">
                    <span className="font-medium">{d.formKind}</span>{" "}
                    <span className="text-slate-500">{d.deadlineKind}</span>{" "}
                    →{" "}
                    <span className="font-semibold">
                      {new Date(d.deadlineDate).toLocaleDateString()}
                    </span>{" "}
                    <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-xs">
                      {d.status}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <pre className="mt-6 max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-700">
            {data.bodyMarkdown}
          </pre>

          <DraftChatPanel draftId={id} onApplied={refresh} />
        </>
      )}
    </div>
  );
}

// CitationCanonicalPopover is the 31.5 surface: when the verifier
// finds a stale or outdated citation it carries the canonical
// replacement text. Iter 1 rendered that text inline, which made the
// citation row noisy and easy to mis-read as the actual cite. Iter 2
// hides the canonical text behind a small "canonical ›" trigger that
// shows it in a popover on hover, focus or click. Click toggles
// (sticky open); blur/escape closes. No external dependency —
// implemented with native React + a small focus trap.
function CitationCanonicalPopover({ canonical }: { canonical: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <span
      ref={ref}
      className="relative ml-2 inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="citation-canonical-popover"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="cursor-help rounded border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 hover:bg-amber-200"
        aria-expanded={open}
        aria-label="Show canonical replacement"
      >
        canonical ›
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-0 top-full z-10 mt-1 w-max max-w-md rounded-md border border-amber-300 bg-white p-2 text-xs text-amber-900 shadow-lg"
        >
          <span className="block font-semibold uppercase tracking-wide text-amber-700">
            canonical replacement
          </span>
          <span className="mt-1 block font-mono">{canonical}</span>
        </span>
      )}
    </span>
  );
}
